import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Mail, Lock, Eye, EyeOff, ShieldCheck, Clock, ChevronLeft } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { SafecheckLogo } from "../components/SafecheckLogo";
import { LoginCarousel } from "../components/LoginCarousel";
import { cn } from "@/shared/lib/cn";
import { loginRequest } from "../api/companyApi";
import { apiRoleToAccountType, getAccountTypeRoute, storeAuth } from "../auth";

const inputClass =
  "h-12 w-full rounded-xl border border-border bg-card-muted pl-11 pr-4 text-sm text-foreground outline-none transition-colors placeholder:text-muted/70 focus:border-accent focus:ring-2 focus:ring-accent/20";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const resetSuccess =
    location.state && typeof location.state === "object" && "message" in location.state
      ? String((location.state as { message?: string }).message ?? "")
      : null;
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const response = await loginRequest({ email: email.trim(), password });
      storeAuth(response.access_token, response.user);
      const accountType = apiRoleToAccountType(response.user.role);
      navigate(getAccountTypeRoute(accountType));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Connexion impossible.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white lg:flex-row">
      {/* Panneau gauche — carrousel desktop */}
      <div className="relative hidden overflow-hidden bg-sidebar-bg lg:flex lg:w-[46%] xl:w-1/2">
        <LoginCarousel />
        <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-between px-10 py-16 xl:px-16">
          <SafecheckLogo size="lg" variant="onDark" className="drop-shadow-md" linkToDashboard={false} />
          <div className="max-w-xs text-center">
            <span className="mx-auto mb-4 block h-1 w-12 rounded-full bg-accent" />
            <p className="text-xl font-medium leading-relaxed text-white/90 drop-shadow-sm">
              {t.safecheck.login.tagline}
            </p>
          </div>
        </div>
      </div>

      {/* En-tête mobile + carrousel */}
      <div className="relative overflow-hidden bg-sidebar-bg lg:hidden">
        <div className="relative h-48 sm:h-56">
          <LoginCarousel />
        </div>
        <div className="absolute inset-x-0 top-0 z-10 px-5 py-5">
          <a
            href="https://safecheck-rdc.com"
            className="inline-flex items-center gap-1.5 text-sm text-white/70 transition-colors hover:text-white"
          >
            <ChevronLeft size={16} />
            {t.safecheck.login.backToHome}
          </a>
        </div>
        <div className="absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 justify-center">
          <SafecheckLogo size="lg" variant="onDark" className="drop-shadow-md" linkToDashboard={false} />
        </div>
      </div>

      {/* Formulaire */}
      <div className="flex flex-1 flex-col justify-center bg-white px-5 py-8 sm:px-10 sm:py-12 lg:px-14 xl:px-20">
        <div className="mx-auto w-full max-w-[400px]">
          <a
            href="https://safecheck-rdc.com"
            className="mb-8 hidden items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground lg:inline-flex"
          >
            <ChevronLeft size={16} />
            {t.safecheck.login.backToHome}
          </a>

          <header className="mb-7 lg:mb-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted">
              {t.safecheck.login.platform}
            </p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              {t.safecheck.login.title}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted">{t.safecheck.login.subtitle}</p>
          </header>

          <form
            onSubmit={handleSubmit}
            className="theme-card theme-card-static space-y-5 p-6 sm:p-7"
          >
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="login-email"
                  className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  {t.safecheck.login.email}
                </label>
                <div className="relative">
                  <Mail
                    size={18}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-primary"
                    aria-hidden
                  />
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    placeholder="votre@email.com"
                    className={inputClass}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="login-password"
                  className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  {t.safecheck.login.password}
                </label>
                <div className="relative">
                  <Lock
                    size={18}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
                    aria-hidden
                  />
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className={cn(inputClass, "pr-11")}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-muted transition-colors hover:text-foreground"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border accent-accent"
                />
                {t.safecheck.login.remember}
              </label>
              <Link
                to="/login/forgot"
                className="text-sm font-medium text-primary underline-offset-4 transition-colors hover:text-primary/80 hover:underline"
              >
                {t.safecheck.login.forgotPassword}
              </Link>
            </div>

            {resetSuccess ? (
              <p className="rounded-xl border border-green-600/30 bg-green-50 px-4 py-3 text-sm text-green-800">
                {resetSuccess}
              </p>
            ) : null}

            {error && (
              <p className="rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="h-12 w-full rounded-xl bg-accent text-sm font-semibold text-accent-foreground shadow-md shadow-accent/20 transition-all hover:bg-accent/90 hover:shadow-lg hover:shadow-accent/25 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting ? "Connexion…" : t.safecheck.login.submit}
            </button>
          </form>

          <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <span className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-xs text-muted-foreground">
              <ShieldCheck size={15} className="shrink-0 text-primary" />
              {t.safecheck.login.secure}
            </span>
            <span className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-xs text-muted-foreground">
              <Clock size={15} className="shrink-0 text-primary" />
              {t.safecheck.login.access}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
